/**
 * Organization Brand Theming & Color Utilities
 * Supports bidirectional color conversions (HEX <-> RGB <-> HSL),
 * WCAG contrast ratio calculations, CSS custom property injection for primary and secondary colors,
 * and persistent storage across the application.
 */

export const DEFAULT_PRIMARY_COLOR = '#038912'; // HiAcuity Emerald
export const DEFAULT_SECONDARY_COLOR = '#093725'; // HiAcuity Deep Forest

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface HSL {
  h: number;
  s: number;
  l: number;
  hslString: string;
}

export interface BrandThemeConfig {
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * Validates a HEX color string (#RGB or #RRGGBB).
 */
export function isValidHex(hex: string): boolean {
  return /^#([A-Fa-f0-9]{3}){1,2}$/.test(hex.trim());
}

/**
 * Normalizes HEX string to 6-digit lowercase with leading '#'.
 */
export function normalizeHex(hex: string, fallback: string = DEFAULT_PRIMARY_COLOR): string {
  if (!hex) return fallback;
  const cleaned = hex.trim().replace(/^#/, '');
  if (cleaned.length === 3) {
    return `#${cleaned[0]}${cleaned[0]}${cleaned[1]}${cleaned[1]}${cleaned[2]}${cleaned[2]}`.toLowerCase();
  }
  if (cleaned.length === 6) {
    return `#${cleaned}`.toLowerCase();
  }
  return fallback;
}

/**
 * Converts HEX color to RGB object.
 */
export function hexToRgb(hex: string): RGB {
  const normalized = normalizeHex(hex);
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  return { r, g, b };
}

/**
 * Converts RGB numbers to 6-digit HEX color string.
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (val: number) => Math.max(0, Math.min(255, Math.round(val)));
  const toHex = (c: number) => clamp(c).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Converts RGB to HSL.
 */
export function rgbToHsl(r: number, g: number, b: number): HSL {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0);
        break;
      case gNorm:
        h = (bNorm - rNorm) / delta + 2;
        break;
      case bNorm:
        h = (rNorm - gNorm) / delta + 4;
        break;
    }
    h = Math.round(h * 60);
  }

  const hDeg = Math.round(h);
  const sPct = Math.round(s * 100);
  const lPct = Math.round(l * 100);

  return {
    h: hDeg,
    s: sPct,
    l: lPct,
    hslString: `hsl(${hDeg}, ${sPct}%, ${lPct}%)`,
  };
}

/**
 * Converts HEX color to HSL object.
 */
export function hexToHsl(hex: string): HSL {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

/**
 * Converts HSL values or HSL string into HEX.
 */
export function hslToHex(hslOrH: string | number, s?: number, l?: number): string {
  let hue = 0;
  let saturation = 0;
  let lightness = 0;

  if (typeof hslOrH === 'string') {
    const match = hslOrH.match(/hsl\s*\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/i);
    if (match) {
      hue = parseFloat(match[1]);
      saturation = parseFloat(match[2]);
      lightness = parseFloat(match[3]);
    } else {
      return DEFAULT_PRIMARY_COLOR;
    }
  } else {
    hue = hslOrH;
    saturation = s ?? 80;
    lightness = l ?? 50;
  }

  hue = ((hue % 360) + 360) % 360;
  const sNorm = saturation / 100;
  const lNorm = lightness / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (hue < 60) {
    rPrime = c;
    gPrime = x;
    bPrime = 0;
  } else if (hue < 120) {
    rPrime = x;
    gPrime = c;
    bPrime = 0;
  } else if (hue < 180) {
    rPrime = 0;
    gPrime = c;
    bPrime = x;
  } else if (hue < 240) {
    rPrime = 0;
    gPrime = x;
    bPrime = c;
  } else if (hue < 300) {
    rPrime = x;
    gPrime = 0;
    bPrime = c;
  } else {
    rPrime = c;
    gPrime = 0;
    bPrime = x;
  }

  const r = Math.round((rPrime + m) * 255);
  const g = Math.round((gPrime + m) * 255);
  const b = Math.round((bPrime + m) * 255);

  return rgbToHex(r, g, b);
}

/**
 * Calculates relative luminance for WCAG contrast measurement.
 */
export function getLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const toLinear = (c: number) => {
    const norm = c / 255;
    return norm <= 0.03928 ? norm / 12.92 : Math.pow((norm + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Calculates WCAG 2.1 contrast ratio between two colors (1:1 to 21:1).
 */
export function getContrastRatio(foregroundHex: string, backgroundHex: string): number {
  const l1 = getLuminance(foregroundHex);
  const l2 = getLuminance(backgroundHex);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Returns accessible text color (#ffffff or #0f172a) based on background luminance.
 */
export function getContrastTextColor(backgroundHex: string): '#ffffff' | '#0f172a' {
  const luminance = getLuminance(backgroundHex);
  return luminance > 0.42 ? '#0f172a' : '#ffffff';
}

/**
 * Adjusts color brightness by percent (-100 to 100).
 */
export function adjustColorBrightness(hex: string, percent: number): string {
  const { r, g, b } = hexToRgb(hex);
  const factor = 1 + percent / 100;
  return rgbToHex(r * factor, g * factor, b * factor);
}

/**
 * Creates an RGBA color string from HEX and alpha.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Generates a full 50-950 Tailwind-compatible color scale for a brand color.
 */
export interface ColorPaletteShades {
  50: { hex: string; rgb: string };
  100: { hex: string; rgb: string };
  200: { hex: string; rgb: string };
  300: { hex: string; rgb: string };
  400: { hex: string; rgb: string };
  500: { hex: string; rgb: string };
  600: { hex: string; rgb: string };
  700: { hex: string; rgb: string };
  800: { hex: string; rgb: string };
  900: { hex: string; rgb: string };
  950: { hex: string; rgb: string };
}

export function generateColorPalette(hex: string): ColorPaletteShades {
  const { r, g, b } = hexToRgb(hex);
  const hsl = rgbToHsl(r, g, b);
  const hue = hsl.h;
  const sat = hsl.s;

  const toShade = (h: number, s: number, l: number) => {
    const shadeHex = hslToHex(h, s, l);
    const rgb = hexToRgb(shadeHex);
    return {
      hex: shadeHex,
      rgb: `${rgb.r} ${rgb.g} ${rgb.b}`,
    };
  };

  return {
    50: toShade(hue, Math.min(sat, 85), 96),
    100: toShade(hue, Math.min(sat, 85), 91),
    200: toShade(hue, Math.min(sat, 80), 82),
    300: toShade(hue, Math.min(sat, 80), 70),
    400: toShade(hue, Math.min(sat, 85), 58),
    500: { hex, rgb: `${r} ${g} ${b}` },
    600: {
      hex: adjustColorBrightness(hex, -10),
      rgb: (() => {
        const c = hexToRgb(adjustColorBrightness(hex, -10));
        return `${c.r} ${c.g} ${c.b}`;
      })(),
    },
    700: {
      hex: adjustColorBrightness(hex, -20),
      rgb: (() => {
        const c = hexToRgb(adjustColorBrightness(hex, -20));
        return `${c.r} ${c.g} ${c.b}`;
      })(),
    },
    800: {
      hex: adjustColorBrightness(hex, -32),
      rgb: (() => {
        const c = hexToRgb(adjustColorBrightness(hex, -32));
        return `${c.r} ${c.g} ${c.b}`;
      })(),
    },
    900: {
      hex: adjustColorBrightness(hex, -48),
      rgb: (() => {
        const c = hexToRgb(adjustColorBrightness(hex, -48));
        return `${c.r} ${c.g} ${c.b}`;
      })(),
    },
    950: {
      hex: adjustColorBrightness(hex, -65),
      rgb: (() => {
        const c = hexToRgb(adjustColorBrightness(hex, -65));
        return `${c.r} ${c.g} ${c.b}`;
      })(),
    },
  };
}

/**
 * Applies primary and secondary colors as CSS variables to the document root and persists to localStorage.
 */
export function applyBrandTheme({
  primaryColor = DEFAULT_PRIMARY_COLOR,
  secondaryColor = DEFAULT_SECONDARY_COLOR,
}: BrandThemeConfig): void {
  if (typeof window === 'undefined') return;

  const validPrimary = isValidHex(primaryColor) ? normalizeHex(primaryColor) : DEFAULT_PRIMARY_COLOR;
  const validSecondary = isValidHex(secondaryColor) ? normalizeHex(secondaryColor) : DEFAULT_SECONDARY_COLOR;

  const primaryRgb = hexToRgb(validPrimary);
  const secondaryRgb = hexToRgb(validSecondary);

  const primaryText = getContrastTextColor(validPrimary);
  const secondaryText = getContrastTextColor(validSecondary);

  const root = document.documentElement;

  // Generate full 50-950 scale for primary color
  const palette = generateColorPalette(validPrimary);
  const shadeKeys = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
  for (const key of shadeKeys) {
    root.style.setProperty(`--brand-primary-${key}`, palette[key].hex);
    root.style.setProperty(`--brand-primary-${key}-rgb`, palette[key].rgb);
  }

  // Primary Theme Variables
  root.style.setProperty('--brand-primary', validPrimary);
  root.style.setProperty('--brand-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  root.style.setProperty('--brand-primary-hover', palette[600].hex);
  root.style.setProperty('--brand-primary-active', palette[700].hex);
  root.style.setProperty('--brand-primary-light', hexToRgba(validPrimary, 0.12));
  root.style.setProperty('--brand-primary-text', primaryText);
  root.style.setProperty('--brand-text-on-primary', primaryText);
  root.style.setProperty('--brand-focus-ring', `0 0 0 3px ${hexToRgba(validPrimary, 0.25)}`);
  root.style.setProperty('--focus-ring', `0 0 0 3px ${hexToRgba(validPrimary, 0.25)}`);

  // Secondary Theme Variables
  root.style.setProperty('--brand-secondary', validSecondary);
  root.style.setProperty('--brand-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  root.style.setProperty('--brand-secondary-hover', adjustColorBrightness(validSecondary, -10));
  root.style.setProperty('--brand-secondary-active', adjustColorBrightness(validSecondary, -18));
  root.style.setProperty('--brand-secondary-light', hexToRgba(validSecondary, 0.12));
  root.style.setProperty('--brand-secondary-text', secondaryText);

  // Navbar Theme Variables (Driven by secondaryColor)
  const isLightNavbar = secondaryText === '#0f172a';

  root.style.setProperty('--navbar-bg', validSecondary);
  root.style.setProperty('--navbar-text', secondaryText);
  root.style.setProperty(
    '--navbar-text-muted',
    isLightNavbar ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.75)'
  );
  root.style.setProperty('--navbar-icon', secondaryText);
  root.style.setProperty(
    '--navbar-btn-bg',
    isLightNavbar ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.12)'
  );
  root.style.setProperty(
    '--navbar-btn-border',
    isLightNavbar ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.22)'
  );
  root.style.setProperty(
    '--navbar-btn-hover',
    isLightNavbar ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.22)'
  );
  root.style.setProperty(
    '--navbar-glass-highlight',
    isLightNavbar
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
  );

  // Gradient variable
  root.style.setProperty(
    '--brand-gradient',
    `linear-gradient(135deg, ${validPrimary} 0%, ${validSecondary} 100%)`
  );

  // Tailwind theme overrides
  root.style.setProperty('--primary-dark', validPrimary);
  root.style.setProperty('--primary-main', validSecondary);

  // Persist to localStorage
  try {
    localStorage.setItem('primaryColor', validPrimary);
    localStorage.setItem('secondaryColor', validSecondary);
    localStorage.setItem('brandPrimaryColor', validPrimary);
    localStorage.setItem('brandSecondaryColor', validSecondary);
    // Notify MUI isolated theme listeners
    window.dispatchEvent(new CustomEvent('brandThemeChange'));
  } catch (err) {
    console.warn('Unable to persist brand theme in localStorage', err);
  }
}

/**
 * Loads and initializes saved brand theme from localStorage.
 */
export function initBrandTheme(): BrandThemeConfig {
  if (typeof window === 'undefined') {
    return {
      primaryColor: DEFAULT_PRIMARY_COLOR,
      secondaryColor: DEFAULT_SECONDARY_COLOR,
    };
  }

  const primary =
    localStorage.getItem('primaryColor') ||
    localStorage.getItem('brandPrimaryColor') ||
    DEFAULT_PRIMARY_COLOR;

  const secondary =
    localStorage.getItem('secondaryColor') ||
    localStorage.getItem('brandSecondaryColor') ||
    DEFAULT_SECONDARY_COLOR;

  applyBrandTheme({ primaryColor: primary, secondaryColor: secondary });

  return {
    primaryColor: primary,
    secondaryColor: secondary,
  };
}
